/**
 * AddPhoneInline.tsx
 *
 * Inline "Add phone" edit control for prospect queue cards and detail panels.
 * Shown when prospect.phone is missing.
 *
 * On save:
 *  UPDATE decision_makers
 *  SET contact_info = jsonb_set(contact_info, '{phone}', '"<number>"')
 *  WHERE id = <contactId>
 *
 * Uses the /api/contacts/:id/phone PATCH endpoint (to be created server-side).
 * Falls back to a console.warn if the endpoint returns 404 so the UI never
 * hard-crashes before the route is wired.
 *
 * Visual states:
 *  - Collapsed: small "Add phone" link
 *  - Expanded: text input + Save / Cancel buttons
 *  - Saving: spinner state
 *  - Saved: green flash, then hides itself
 *
 * Props:
 *  - contactId: decision_makers.id (bigint)
 *  - onSaved: called with the new phone number string
 */

import { useState } from "react";
import { api } from "@/lib/session";

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

export interface AddPhoneInlineProps {
  contactId: number | null;
  onSaved: (phone: string) => void;
}

export default function AddPhoneInline({ contactId, onSaved }: AddPhoneInlineProps) {
  const [open, setOpen]   = useState(false);
  const [phone, setPhone] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[10px] font-semibold uppercase tracking-wider underline-offset-2 hover:underline"
        style={{ color: MI6_RED }}
      >
        + Add phone
      </button>
    );
  }

  async function handleSave() {
    const trimmed = phone.trim();
    if (!trimmed) return;
    if (contactId === null) {
      setErrMsg("No contact record linked — save prospect first.");
      setState("error");
      return;
    }

    setState("saving");
    setErrMsg("");

    try {
      await api(`/api/contacts/${contactId}/phone`, {
        method: "PATCH",
        body: JSON.stringify({ phone: trimmed }),
      });
      setState("saved");
      onSaved(trimmed);
      setTimeout(() => {
        setOpen(false);
        setPhone("");
        setState("idle");
      }, 1200);
    } catch (e: any) {
      // If endpoint isn't wired yet, degrade gracefully
      if (e?.status === 404) {
        console.warn("[AddPhoneInline] /api/contacts/:id/phone not yet implemented — updating locally only.");
        setState("saved");
        onSaved(trimmed);
        setTimeout(() => {
          setOpen(false);
          setPhone("");
          setState("idle");
        }, 1200);
      } else {
        setErrMsg(e?.message ?? "Could not save phone number.");
        setState("error");
      }
    }
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") { setOpen(false); setPhone(""); setState("idle"); }
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <input
        type="tel"
        value={phone}
        onChange={(e) => { setPhone(e.target.value); setState("idle"); setErrMsg(""); }}
        onKeyDown={handleKey}
        placeholder="555-867-5309"
        autoFocus
        className="text-xs rounded border border-neutral-300 px-2 py-0.5 w-36 focus:outline-none focus:border-neutral-500"
        style={{ color: MI6_BLACK }}
        disabled={state === "saving" || state === "saved"}
      />
      <button
        onClick={handleSave}
        disabled={state === "saving" || state === "saved" || !phone.trim()}
        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white disabled:opacity-40 transition-colors"
        style={{
          background: state === "saved" ? "#16A34A" : MI6_RED,
        }}
      >
        {state === "saving" ? "..." : state === "saved" ? "Saved" : "Save"}
      </button>
      <button
        onClick={() => { setOpen(false); setPhone(""); setState("idle"); }}
        disabled={state === "saving"}
        className="text-[10px] text-neutral-500 hover:text-neutral-600"
      >
        Cancel
      </button>
      {errMsg && (
        <span className="text-[10px] w-full" style={{ color: MI6_RED }}>{errMsg}</span>
      )}
    </div>
  );
}
